/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  Box,
  Button,
  Collapse,
  Divider,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Icon,
  Image,
  Spinner,
  Text,
  Tooltip,
  VStack,
  useBreakpointValue,
} from '@chakra-ui/react';
import { setAccessToken } from '@partiu/api/axios.ts';
import { IUser } from '@partiu/api/types';
import React, { useEffect, useMemo, useState } from 'react';
import { IconType } from 'react-icons';
import { FaChevronDown, FaChevronRight, FaPowerOff } from 'react-icons/fa6';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeProvider.tsx';

export interface IMenuLink {
  display: string;
  path: string;
  icon: IconType;
  canShow?: (user: IUser) => boolean;
  isExternal?: boolean;
  showOnQuickMenu?: boolean;
  showOnTopMenu?: boolean;
}

export interface IMenuSection {
  section: string;
  icon?: IconType;
  items: IMenuLink[];
}

export const SIDEBAR_RAIL_WIDTH = '64px';
export const SIDEBAR_EXPANDED_WIDTH = '300px';

export function DrawerMenu({ links, profile }: { links?: (IMenuLink | IMenuSection)[]; profile?: IUser | null }) {
  const { isMenuOpen, setMenuOpen, isSidebarCompact, setSidebarCompact, logo, colors, resetTheme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const isDesktop = useBreakpointValue({ base: false, md: true });
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set());

  useEffect(() => {
    try {
      const savedSections = localStorage.getItem('drawerMenuExpandedSections');
      if (savedSections) {
        setExpandedSections(new Set(JSON.parse(savedSections)));
      }
    } catch (error) {
      console.warn('Failed to load expanded sections from localStorage:', error);
    }
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem('drawerMenuExpandedSections', JSON.stringify(Array.from(expandedSections)));
    } catch (error) {
      console.warn('Failed to save expanded sections to localStorage:', error);
    }
  }, [expandedSections]);

  const visibleLinks = useMemo(() => {
    if (!links) return [];

    return links
      .map((item) => {
        if ('section' in item) {
          return {
            ...item,
            items: item.items.filter((link) => (link.canShow && profile ? link.canShow(profile) : true)),
          };
        }
        if (item.canShow && profile) {
          return item.canShow(profile) ? item : null;
        }
        return item;
      })
      .filter(Boolean) as (IMenuLink | IMenuSection)[];
  }, [links, profile]);

  const handleMenuClick = (link: IMenuLink) => {
    if (!isDesktop) {
      setMenuOpen(false);
    }

    setTimeout(() => {
      if (!link.isExternal) {
        navigate(link.path);
      } else {
        window.open(link.path, '_blank');
      }
    }, 100);
  };

  const handleLogout = () => {
    if (!isDesktop) {
      setMenuOpen(false);
    }

    setTimeout(() => {
      resetTheme();
      setAccessToken(null);
      navigate('/');
    }, 100);
  };

  const toggleSection = (sectionName: string) => {
    setExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(sectionName)) {
        next.delete(sectionName);
      } else {
        next.add(sectionName);
      }
      return next;
    });
  };

  const openSection = (sectionName: string) => {
    setExpandedSections((prev) => new Set(prev).add(sectionName));
  };

  useEffect(() => {
    if (!links) return;

    const sectionsToExpand = new Set<string>();
    links.forEach((item) => {
      if ('section' in item) {
        const hasActiveItem = item.items.some((link) => location.pathname === link.path);
        if (hasActiveItem) sectionsToExpand.add(item.section);
      }
    });

    if (sectionsToExpand.size > 0) {
      setExpandedSections((prev) => {
        const next = new Set(prev);
        sectionsToExpand.forEach((section) => next.add(section));
        return next;
      });
    }
  }, [location.pathname, links]);

  const renderMenuItem = (link: IMenuLink, isSubItem = false) => (
    <Button
      key={link.path}
      variant={'ghost'}
      height={'40px'}
      width={'100%'}
      borderRadius={'8px'}
      mx={0}
      mb={isSubItem ? 0 : 1.5}
      justifyContent="flex-start"
      backgroundColor={location.pathname === link.path ? `${colors.primaryColor}15` : 'transparent'}
      color={location.pathname === link.path ? colors.primaryColor : 'gray.600'}
      transition={'all 0.2s ease-in-out'}
      onClick={() => handleMenuClick(link)}
      leftIcon={React.createElement(link.icon, {
        size: 16,
        style: { marginRight: 8 },
        color: location.pathname === link.path ? colors.primaryColor : 'gray.500',
      })}
      _hover={{
        backgroundColor: location.pathname === link.path ? `${colors.primaryColor}20` : 'gray.50',
        transform: 'translateX(3px)',
      }}
      _active={{
        backgroundColor: `${colors.primaryColor}25`,
        transform: 'translateX(2px)',
      }}
      fontSize="sm"
      fontWeight={location.pathname === link.path ? '700' : '500'}
      position="relative"
      _before={
        location.pathname === link.path
          ? {
              content: '""',
              position: 'absolute',
              left: '-3px',
              top: '50%',
              transform: 'translateY(-50%)',
              width: '5px',
              height: '24px',
              backgroundColor: colors.primaryColor,
              borderRadius: '3px',
            }
          : {}
      }
    >
      {link.display}
    </Button>
  );

  const renderSection = (section: IMenuSection) => {
    if (section.items.length === 0) return null;

    const isExpanded = expandedSections.has(section.section);
    const hasActiveItem = section.items.some((item) => location.pathname === item.path);

    return (
      <Box key={section.section} width="100%" mb={1.5}>
        <Button
          variant={'ghost'}
          height={'44px'}
          width={'100%'}
          borderRadius={'8px'}
          mx={0}
          justifyContent="flex-start"
          border="1px solid"
          borderColor={hasActiveItem ? `${colors.primaryColor}55` : 'gray.200'}
          backgroundColor={hasActiveItem ? `${colors.primaryColor}12` : 'gray.50'}
          color={hasActiveItem ? colors.primaryColor : 'gray.800'}
          transition={'all 0.2s ease-in-out'}
          onClick={() => toggleSection(section.section)}
          _hover={{
            backgroundColor: hasActiveItem ? `${colors.primaryColor}18` : 'gray.100',
            borderColor: hasActiveItem ? `${colors.primaryColor}77` : 'gray.300',
          }}
          leftIcon={
            section.icon
              ? React.createElement(section.icon, {
                  size: 16,
                  style: { marginRight: 8 },
                  color: hasActiveItem ? colors.primaryColor : 'gray.600',
                })
              : undefined
          }
        >
          <Text
            fontSize="xs"
            fontWeight="700"
            textTransform="uppercase"
            letterSpacing="0.06em"
            flex="1"
            textAlign="left"
          >
            {section.section}
          </Text>
          <Icon
            as={isExpanded ? FaChevronDown : FaChevronRight}
            boxSize={3}
            color={hasActiveItem ? colors.primaryColor : 'gray.400'}
            transition="transform 0.2s ease-in-out"
          />
        </Button>

        <Collapse in={isExpanded} animateOpacity>
          <Box mt={1} mb={1} ml={4} pl={2} borderLeft="1px solid" borderColor="gray.200">
            <VStack spacing={1} align="stretch">
              {section.items.map((item) => renderMenuItem(item, true))}
            </VStack>
          </Box>
        </Collapse>
      </Box>
    );
  };

  const renderRailItem = (item: IMenuLink | IMenuSection) => {
    const isSection = 'section' in item;

    if (isSection && item.items.length === 0) return null;

    const key = isSection ? item.section : item.path;
    const label = isSection ? item.section : item.display;
    const icon = isSection ? item.icon : item.icon;
    const isActive = isSection
      ? item.items.some((link) => location.pathname === link.path)
      : location.pathname === item.path;

    return (
      <Tooltip key={key} label={label} placement="right" hasArrow openDelay={300}>
        <Button
          variant="ghost"
          height="44px"
          width="44px"
          minWidth="44px"
          borderRadius="8px"
          padding={0}
          backgroundColor={isActive ? `${colors.primaryColor}15` : 'transparent'}
          color={isActive ? colors.primaryColor : 'gray.500'}
          onClick={() => {
            if (isSection) {
              openSection(item.section);
              setSidebarCompact(false);
            } else {
              handleMenuClick(item);
            }
          }}
          _hover={{ backgroundColor: isActive ? `${colors.primaryColor}20` : 'gray.100' }}
        >
          {icon ? React.createElement(icon, { size: 20 }) : null}
        </Button>
      </Tooltip>
    );
  };

  const renderExpandedContent = () => (
    <VStack spacing={0} height={'100%'} overflow="hidden">
      <Box flex="1" width="100%" overflowY="auto" px={3}>
        {visibleLinks.length === 0 ? (
          <VStack spacing={4} py={8} px={4}>
            <Spinner size="md" color={colors.primaryColor} />
            <Text color="gray.600" textAlign="center" fontSize="sm">
              Carregando itens do menu...
            </Text>
          </VStack>
        ) : (
          <Box overflow="hidden">
            <VStack spacing={0} py={2}>
              {visibleLinks.map((item) => ('section' in item ? renderSection(item) : renderMenuItem(item)))}
            </VStack>
          </Box>
        )}
      </Box>

      <Divider flexShrink={0} my={2} />

      <Box overflow="hidden" flexShrink={0} px={3}>
        <Button
          variant={'ghost'}
          height={'40px'}
          mx={0}
          mb={3}
          width={'100%'}
          borderRadius={'8px'}
          justifyContent="flex-start"
          onClick={handleLogout}
          leftIcon={<FaPowerOff size={16} style={{ marginRight: 8 }} />}
          color="red.500"
          _hover={{ backgroundColor: 'red.50', transform: 'translateX(3px)' }}
          _active={{ backgroundColor: 'red.100', transform: 'translateX(2px)' }}
          transition={'all 0.2s ease-in-out'}
          fontSize="sm"
          fontWeight="600"
        >
          Sair
        </Button>
      </Box>
    </VStack>
  );

  if (!isDesktop) {
    return (
      <Drawer placement={'left'} onClose={() => setMenuOpen(false)} isOpen={isMenuOpen}>
        <DrawerOverlay />
        <DrawerContent overflow="hidden">
          <DrawerHeader borderBottomWidth="1px" flexShrink={0} py={4}>
            <Image width={'100%'} height={'50px'} objectFit={'contain'} src={logo} />
          </DrawerHeader>
          <DrawerBody padding={'0px'} display="flex" flexDirection="column" height="100%">
            {renderExpandedContent()}
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Box
      position="fixed"
      left={0}
      top={0}
      height="100vh"
      width={isSidebarCompact ? SIDEBAR_RAIL_WIDTH : SIDEBAR_EXPANDED_WIDTH}
      backgroundColor="white"
      borderRight="1px solid"
      borderColor="gray.200"
      transition="width 0.2s ease-in-out"
      overflow="hidden"
      zIndex={20}
      display="flex"
      flexDirection="column"
    >
      {isSidebarCompact ? (
        <VStack spacing={2} py={3} px={2} height="100%" overflowY="auto">
          {visibleLinks.map((item) => renderRailItem(item))}
        </VStack>
      ) : (
        <>
          <Box flexShrink={0} borderBottomWidth="1px" borderColor="gray.200" py={4} px={3}>
            <Image width={'100%'} height={'40px'} objectFit={'contain'} src={logo} />
          </Box>
          <Box flex="1" overflow="hidden">
            {renderExpandedContent()}
          </Box>
        </>
      )}
    </Box>
  );
}
